import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { useInitializeLoyaltyDefaults, useLoyaltyRules, useLoyaltyTiers } from '../../hooks';
import { formatPoints } from '../../lib/format';
import { useLoyaltyPermissions } from '../../lib/useLoyaltyPermissions';
import type { LoyaltyRule, LoyaltyTier } from '../../model/types';
import { EnabledChip, RuleTypeChip } from '../components/chips';
import { CodeText, SectionHeader } from '../components/primitives';
import { ProgramModelCard } from '../components/ProgramModelCard';
import { RuleFormDialog } from '../components/RuleFormDialog';
import { TierFormDialog } from '../components/TierFormDialog';

function paramsSummary(params?: Record<string, unknown>): string {
  if (!params) return '—';
  const entries = Object.entries(params);
  if (entries.length === 0) return '—';
  return entries
    .map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
    .join(', ');
}

/** Loyalty programme administration — model, tier ladder + rules (gated by `loyalty.view`). */
export function LoyaltyProgramPage() {
  const { t } = useTranslation('loyalty');
  const { canView, canManage } = useLoyaltyPermissions();
  const tiers = useLoyaltyTiers({ enabled: canView });
  const rules = useLoyaltyRules({ enabled: canView });
  const initialize = useInitializeLoyaltyDefaults();

  const [tierDialog, setTierDialog] = useState<{ open: boolean; tier: LoyaltyTier | null }>({
    open: false,
    tier: null,
  });
  const [ruleDialog, setRuleDialog] = useState<{ open: boolean; rule: LoyaltyRule | null }>({
    open: false,
    rule: null,
  });
  const [toast, setToast] = useState<string | null>(null);

  if (!canView) {
    return (
      <PageContainer title={t('program.title')}>
        <ErrorState
          title={t('program.accessRestrictedTitle')}
          message={t('program.accessRestrictedMessage')}
        />
      </PageContainer>
    );
  }

  const isLoading = tiers.isLoading || rules.isLoading;
  const isError = tiers.isError || rules.isError;
  const tierList = tiers.data ?? [];
  const ruleList = rules.data ?? [];
  const isEmpty = tierList.length === 0 && ruleList.length === 0;

  const seedDefaults = () =>
    initialize.mutate(undefined, {
      onSuccess: () => setToast(t('program.seedDefaultsSuccess')),
      onError: (err) => setToast(err.message),
    });

  return (
    <PageContainer title={t('program.title')} subtitle={t('program.subtitle')}>
      {!canManage ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('program.viewOnlyNotice')}
        </Alert>
      ) : null}

      <Stack spacing={3}>
        <ProgramModelCard canView={canView} canManage={canManage} onToast={(m) => setToast(m)} />

        {isLoading ? (
          <LoadingState label={t('program.loading')} />
        ) : isError ? (
          <ErrorState
            title={t('program.loadError.title')}
            message={tiers.error?.message ?? rules.error?.message ?? t('common:error.unknown')}
            onRetry={() => {
              void tiers.refetch();
              void rules.refetch();
            }}
          />
        ) : isEmpty ? (
          <EmptyState
            title={t('program.empty.title')}
            description={t('program.empty.description')}
            action={
              canManage ? (
                <Button
                  variant="contained"
                  startIcon={
                    initialize.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AutoAwesomeRoundedIcon />
                    )
                  }
                  disabled={initialize.isPending}
                  onClick={seedDefaults}
                >
                  {t('program.empty.seedDefaultsAction')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Tiers */}
            <Paper variant="outlined">
              <SectionHeader
                title={t('program.tiers.title')}
                onAdd={canManage ? () => setTierDialog({ open: true, tier: null }) : undefined}
                addLabel={t('program.tiers.addAction')}
              />
              {tierList.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('program.tiers.empty')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('program.tiers.table.code')}</TableCell>
                        <TableCell>{t('program.tiers.table.name')}</TableCell>
                        <TableCell align="right">{t('program.tiers.table.minPoints')}</TableCell>
                        <TableCell>{t('program.tiers.table.benefits')}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tierList.map((tier) => (
                        <TableRow key={tier.id} hover>
                          <TableCell>
                            <CodeText>{tier.code}</CodeText>
                          </TableCell>
                          <TableCell>{tier.name}</TableCell>
                          <TableCell align="right">{formatPoints(tier.minPoints)}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ maxWidth: 320 }}
                              noWrap
                            >
                              {tier.benefits ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {canManage ? (
                              <Tooltip title={t('common:action.edit')}>
                                <IconButton
                                  size="small"
                                  aria-label={t('program.tiers.editAria', { code: tier.code })}
                                  onClick={() => setTierDialog({ open: true, tier })}
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Rules */}
            <Paper variant="outlined">
              <SectionHeader
                title={t('program.rules.title')}
                onAdd={canManage ? () => setRuleDialog({ open: true, rule: null }) : undefined}
                addLabel={t('program.rules.addAction')}
              />
              {ruleList.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('program.rules.empty')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('program.rules.table.code')}</TableCell>
                        <TableCell>{t('program.rules.table.name')}</TableCell>
                        <TableCell>{t('program.rules.table.type')}</TableCell>
                        <TableCell>{t('program.rules.table.status')}</TableCell>
                        <TableCell>{t('program.rules.table.params')}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ruleList.map((rule) => (
                        <TableRow key={rule.id} hover>
                          <TableCell>
                            <CodeText>{rule.code}</CodeText>
                          </TableCell>
                          <TableCell>{rule.name}</TableCell>
                          <TableCell>
                            <RuleTypeChip type={rule.type} />
                          </TableCell>
                          <TableCell>
                            <EnabledChip enabled={rule.enabled} />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ maxWidth: 320 }}
                              noWrap
                            >
                              {paramsSummary(rule.params)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {canManage ? (
                              <Tooltip title={t('common:action.edit')}>
                                <IconButton
                                  size="small"
                                  aria-label={t('program.rules.editAria', { code: rule.code })}
                                  onClick={() => setRuleDialog({ open: true, rule })}
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Stack>

      <TierFormDialog
        open={tierDialog.open}
        tier={tierDialog.tier}
        onClose={() => setTierDialog({ open: false, tier: null })}
        onDone={(message) => setToast(message)}
      />
      <RuleFormDialog
        open={ruleDialog.open}
        rule={ruleDialog.rule}
        onClose={() => setRuleDialog({ open: false, rule: null })}
        onDone={(message) => setToast(message)}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
